import React, { createContext, useContext, useEffect, useRef } from 'react';
import { authService } from '../auth-config/auth-service';

interface FocusContextType {
  logCustomFocusEvent: (eventType: string) => Promise<void>;
}

const FocusTrackingContext = createContext<FocusContextType>({
  logCustomFocusEvent: async () => {},
});

export const useFocusTracking = () => useContext(FocusTrackingContext);

interface ProviderProps {
  children: React.ReactNode;
}

export const FocusTrackingProvider: React.FC<ProviderProps> = ({ children }) => {
  const departedAtRef = useRef<Date | null>(null);
  const alertSentRef = useRef<boolean>(false);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const alertTimerRef = useRef<NodeJS.Timeout | null>(null);

  const DISTRACTION_ALERT_MS = 10 * 60 * 1000; // 10 minutes

  const sendFocusPayload = async (endpoint: string, payload: any) => {
    try {
      const token = await authService.getAccessToken();
      if (!token) return;

      await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.error('Failed to report focus tracking payload', err);
    }
  };

  const triggerAlertIfNeeded = async (reason: string) => {
    if (alertSentRef.current) return;
    alertSentRef.current = true;
    await sendFocusPayload('/api/student/focus/report-distraction', {
      eventType: reason,
      departedAt: departedAtRef.current ? departedAtRef.current.toISOString() : new Date().toISOString()
    });
  };

  const handleDeparture = (reason: string) => {
    if (!departedAtRef.current) {
      departedAtRef.current = new Date();
      alertSentRef.current = false;

      // Start 10-minute alert timer
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
      alertTimerRef.current = setTimeout(() => {
        triggerAlertIfNeeded(reason);
      }, DISTRACTION_ALERT_MS);
    }
  };

  const handleReturn = () => {
    if (departedAtRef.current) {
      const now = new Date();
      const durationSecs = Math.floor((now.getTime() - departedAtRef.current.getTime()) / 1000);

      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);

      sendFocusPayload('/api/student/focus/log-event', {
        eventType: 'session_return',
        departedAt: departedAtRef.current.toISOString(),
        durationSeconds: durationSecs
      });

      departedAtRef.current = null;
      alertSentRef.current = false;
    }
  };

  const resetIdleTimer = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    // If student was previously marked idle/departed, return them
    if (departedAtRef.current && document.visibilityState === 'visible' && document.hasFocus()) {
      handleReturn();
    }

    // Set 10-minute idle timer
    idleTimerRef.current = setTimeout(() => {
      handleDeparture('10min_idle_timeout');
    }, DISTRACTION_ALERT_MS);
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleDeparture('tab_hidden');
      } else {
        handleReturn();
      }
    };

    const handleWindowBlur = () => {
      handleDeparture('window_blur');
    };

    const handleWindowFocus = () => {
      handleReturn();
    };

    const handleUserActivity = () => {
      resetIdleTimer();
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);

    resetIdleTimer();

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);

      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);

      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    };
  }, []);

  const logCustomFocusEvent = async (eventType: string) => {
    await sendFocusPayload('/api/student/focus/log-event', {
      eventType,
      departedAt: new Date().toISOString()
    });
  };

  return (
    <FocusTrackingContext.Provider value={{ logCustomFocusEvent }}>
      {children}
    </FocusTrackingContext.Provider>
  );
};
